import { deleteJson, getJson, patchJson, postJson, putJson } from '@/shared/api/apiClient';

/*
 * Shelves — the user's own grouping of library items ("books", "work",
 * "school-related"), plus the two built-ins the design shows: Saved and
 * Favorites.
 *
 * Backed by `/api/v1/shelves` (valoi_engine app/routers/shelves_api.py). This
 * module used to be localStorage, and its signatures were written for the
 * endpoint it would become — everything async, every mutation returning the
 * whole new collection — so the swap was confined to this file and no caller
 * changed. Shelves are per account now rather than per browser, which is the
 * point: the previous version was invisible from a second device and gone on a
 * cache clear.
 *
 * `userId` is still the first argument of every call and is ignored on the wire:
 * identity comes from the Bearer JWT. It stays in the signatures because every
 * caller passes it, and churning them to remove an argument would be a worse
 * diff than keeping it.
 */

const SHELVES_PATH = '/api/v1/shelves';

/** The six swatches from the Figma shelf `···` menu. */
export const SHELF_COLORS = ['gray', 'coral', 'yellow', 'green', 'blue', 'purple'] as const;
export type ShelfColor = (typeof SHELF_COLORS)[number];

export type Shelf = {
  id: string;
  name: string;
  color: ShelfColor;
  /** Built-ins cannot be renamed, recoloured or deleted. */
  builtin?: 'saved' | 'favorites';
  createdAt: string;
};

/** itemId -> shelfIds. An item can sit on more than one shelf. */
export type ShelfMembership = Record<string, string[]>;

export const SAVED_SHELF_ID = '__saved';
export const FAVORITES_SHELF_ID = '__favorites';

/* -------------------------------------------------------------------------
   Reads
   ------------------------------------------------------------------------- */

/**
 * Custom shelves in creation order, followed by the built-ins.
 *
 * The server synthesises the built-ins and returns them last; ShelfPanel renders
 * that order verbatim ("All, the user's shelves, then Saved and Favorites"), so
 * this deliberately does not re-sort.
 */
export async function listShelves(_userId: string): Promise<Shelf[]> {
  return getJson<Shelf[]>(SHELVES_PATH);
}

export async function getMembership(_userId: string): Promise<ShelfMembership> {
  return getJson<ShelfMembership>(`${SHELVES_PATH}/membership`);
}

/* -------------------------------------------------------------------------
   Shelf writes — each returns the full list, the way the local version did
   ------------------------------------------------------------------------- */

export async function createShelf(
  _userId: string,
  name: string,
  color: ShelfColor = 'gray',
): Promise<Shelf[]> {
  const trimmed = name.trim();
  // Also checked server-side; not spending a round-trip on an empty name is free.
  if (!trimmed) return listShelves(_userId);
  // A duplicate name is a silent no-op server-side rather than a 409 — it is
  // nearly always a mis-click on "Start a new shelf", and there is no error path
  // here that could render one.
  return postJson<Shelf[]>(SHELVES_PATH, { name: trimmed, color });
}

export async function renameShelf(
  _userId: string,
  shelfId: string,
  name: string,
): Promise<Shelf[]> {
  const trimmed = name.trim();
  if (!trimmed) return listShelves(_userId);
  return patchJson<Shelf[]>(`${SHELVES_PATH}/${encodeURIComponent(shelfId)}`, {
    name: trimmed,
  });
}

export async function recolorShelf(
  _userId: string,
  shelfId: string,
  color: ShelfColor,
): Promise<Shelf[]> {
  return patchJson<Shelf[]>(`${SHELVES_PATH}/${encodeURIComponent(shelfId)}`, { color });
}

export async function deleteShelf(_userId: string, shelfId: string): Promise<Shelf[]> {
  // Membership rows go with it server-side (ON DELETE CASCADE), so a later shelf
  // reusing the id cannot inherit the old contents.
  return deleteJson<Shelf[]>(`${SHELVES_PATH}/${encodeURIComponent(shelfId)}`);
}

/* -------------------------------------------------------------------------
   Membership writes
   ------------------------------------------------------------------------- */

/*
 * All four of these end in one PUT of the item's complete shelf set.
 *
 * The local version threaded a single read through each operation rather than
 * letting them call each other, because that turned one tap on a heart into
 * three reads of the whole membership map. The same concern survives the move to
 * HTTP and gets the same answer: read once, compute the new set here, send it —
 * one GET and one PUT, never a chain.
 */
async function put(itemId: string, shelfIds: string[]): Promise<ShelfMembership> {
  return putJson<ShelfMembership>(`${SHELVES_PATH}/items/${encodeURIComponent(itemId)}`, {
    shelf_ids: shelfIds,
  });
}

export async function setItemShelves(
  _userId: string,
  itemId: string,
  shelfIds: string[],
): Promise<ShelfMembership> {
  return put(itemId, [...new Set(shelfIds)]);
}

export async function addItemToShelf(
  userId: string,
  itemId: string,
  shelfId: string,
): Promise<ShelfMembership> {
  const membership = await getMembership(userId);
  return put(itemId, [...new Set([...(membership[itemId] ?? []), shelfId])]);
}

export async function removeItemFromShelf(
  userId: string,
  itemId: string,
  shelfId: string,
): Promise<ShelfMembership> {
  const membership = await getMembership(userId);
  return put(
    itemId,
    (membership[itemId] ?? []).filter((id) => id !== shelfId),
  );
}

export async function toggleFavorite(
  userId: string,
  itemId: string,
): Promise<ShelfMembership> {
  const membership = await getMembership(userId);
  const current = membership[itemId] ?? [];
  return put(
    itemId,
    current.includes(FAVORITES_SHELF_ID)
      ? current.filter((id) => id !== FAVORITES_SHELF_ID)
      : [...current, FAVORITES_SHELF_ID],
  );
}

/* -------------------------------------------------------------------------
   Derivations — pure, and deliberately still client-side
   ------------------------------------------------------------------------- */

/** How many of `itemIds` sit on each shelf, keyed by shelf id. */
export function countsByShelf(
  membership: ShelfMembership,
  itemIds: string[],
): Record<string, number> {
  const present = new Set(itemIds);
  const counts: Record<string, number> = {};
  for (const [itemId, shelfIds] of Object.entries(membership)) {
    // Only count items the caller actually has — a membership row can outlive
    // the item it points at (deleted server-side, evicted from the cache).
    if (!present.has(itemId)) continue;
    for (const shelfId of shelfIds) {
      counts[shelfId] = (counts[shelfId] ?? 0) + 1;
    }
  }
  return counts;
}

export function itemIsOnShelf(
  membership: ShelfMembership,
  itemId: string,
  shelfId: string,
): boolean {
  return (membership[itemId] ?? []).includes(shelfId);
}
